import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Wrench } from "@phosphor-icons/react";

// Recovery links open here as {APP}/reset-password?token_hash=...&type=recovery.
// The token is verified and the new password is set server-side (no browser auth
// session needed), so this page never depends on the Supabase JS auth client.
export default function ResetPasswordPage() {
  const nav = useNavigate();
  // Capture the token ONCE from the URL before any cleanup (survives StrictMode double-invoke).
  const [tokenHash] = useState(() => {
    const s = new URLSearchParams(window.location.search);
    let th = s.get("token_hash");
    if (!th && window.location.hash) th = new URLSearchParams(window.location.hash.slice(1)).get("token_hash");
    return th || null;
  });
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Strip the token from the visible URL / history once captured.
    if (tokenHash) window.history.replaceState({}, document.title, "/reset-password");
  }, [tokenHash]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) return setErr("Use at least 8 characters.");
    if (password !== confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token_hash: tokenHash, password });
      setPassword(""); setConfirm(""); setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      if (detail === "invalid_or_expired") setInvalid(true);
      else setErr(detail || "Could not update password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 bg-blue-600 flex items-center justify-center rounded">
            <Wrench size={28} weight="bold" className="text-white" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-black text-white tracking-tight leading-none">TMIC OPS</h1>
            <p className="font-body text-sm text-slate-400">Operations & Compliance</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border-2 border-slate-300 flex flex-col gap-4" data-testid="reset-form">
          {children}
        </div>
      </div>
    </div>
  );

  if (invalid || !tokenHash) return shell(
    <div className="flex flex-col gap-4" data-testid="reset-invalid">
      <h2 className="font-display text-xl font-bold text-slate-900">Link expired or invalid</h2>
      <p className="font-body text-sm text-slate-700">
        This password reset link has expired or has already been used. Please request a new one.
      </p>
      <Link data-testid="reset-request-again" to="/forgot-password"
        className="h-12 flex items-center justify-center text-base font-bold uppercase tracking-wide bg-slate-900 hover:bg-slate-800 text-white rounded">
        Request a new link
      </Link>
    </div>
  );

  if (done) return shell(
    <div className="flex flex-col gap-3" data-testid="reset-done">
      <h2 className="font-display text-xl font-bold text-slate-900">Password updated</h2>
      <p className="font-body text-sm text-slate-700">Your password has been changed. Redirecting you to sign in…</p>
      <Link data-testid="reset-go-login" to="/login" className="text-blue-600 font-semibold text-sm">Go to sign in now</Link>
    </div>
  );

  return shell(
    <form onSubmit={submit} className="flex flex-col gap-4">
      <h2 className="font-display text-xl font-bold text-slate-900">Set New Password</h2>
      <div>
        <label className="font-body text-sm font-semibold text-slate-700">New password</label>
        <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="h-12 text-base border-2 mt-1" required minLength={8} autoComplete="new-password" />
      </div>
      <div>
        <label className="font-body text-sm font-semibold text-slate-700">Confirm new password</label>
        <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          className="h-12 text-base border-2 mt-1" required minLength={8} autoComplete="new-password" />
      </div>
      {err && <p data-testid="reset-error" className="text-sm text-red-600 font-semibold">{err}</p>}
      <Button data-testid="reset-submit" type="submit" disabled={busy}
        className="h-12 text-base font-bold uppercase tracking-wide bg-slate-900 hover:bg-slate-800">
        {busy ? "Updating…" : "Update password"}
      </Button>
    </form>
  );
}
